import {
  Box,
  Button as ChakraButton,
  Center,
  Icon,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Modal, showToast, Text } from '@diamond/shared/ui';
import { formatCurrencyIdr, unixToHumanDate } from '@diamond/shared/utils';
import ChevronRight from '@mui/icons-material/ChevronRight';
import type { ColDef } from 'ag-grid-community';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useSearchLHCItemMutationsMutation } from '../../hooks';
import type {
  SearchNomorMutasiApiItem,
  SearchNomorMutasiModalProps,
  SearchNomorMutasiRowData,
  SearchNomorMutasiSelectionPayload,
} from '../../types';
import { CollectionSearchResultsSection } from '../collection-search-results-section';
import {
  MutationContextSummary,
  MutationDetailDrawer,
  MutationSearchFilters,
} from './mutation-modal';

const MIN_SEARCH_LENGTH = 3;

function parseDateToUnixSeconds(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const ddmmyy = /^(\d{2})\/(\d{2})\/(\d{2})$/;
  const fullYearMatch = trimmed.match(ddmmyyyy);
  const shortYearMatch = trimmed.match(ddmmyy);
  const matched = fullYearMatch || shortYearMatch;

  if (matched) {
    const day = Number(matched[1]);
    const month = Number(matched[2]);
    const year =
      matched[3].length === 2 ? 2000 + Number(matched[3]) : Number(matched[3]);

    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      return Math.floor(new Date(year, month - 1, day).getTime() / 1000);
    }
  }

  const asNumber = Number(trimmed);
  if (Number.isFinite(asNumber)) {
    return asNumber > 1_000_000_000_000
      ? Math.floor(asNumber / 1000)
      : Math.floor(asNumber);
  }

  const parsed = Date.parse(trimmed);
  if (Number.isNaN(parsed)) return undefined;

  return Math.floor(parsed / 1000);
}

function parseAmountToNumber(value: string): number | undefined {
  const normalized = value.replace(/[^\d]/g, '');
  if (!normalized) return undefined;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatDisplayAmount(value: string): string {
  const numeric = value.replace(/[^\d]/g, '');
  if (!numeric) return '';

  return numeric.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function normalizeUnixSeconds(value: number): number {
  return value > 1_000_000_000_000 ? Math.floor(value / 1000) : value;
}

function mapSearchItem(
  item: SearchNomorMutasiApiItem
): SearchNomorMutasiRowData {
  return {
    mutationId: item.id,
    mutationDate: item.mutation_date
      ? unixToHumanDate(normalizeUnixSeconds(item.mutation_date), 'd MMM yy')
      : '-',
    mutationValue: formatCurrencyIdr(item.amount),
    mutationNo: item.mutation_no || '-',
    bank: item.bank_name || '-',
    memoLine: item.memo_line || '-',
    remark: item.remark || item.usage_summary || '-',
    remaining: formatCurrencyIdr(item.remaining_amount),
  };
}

export function SearchNomorMutasiModal({
  isOpen,
  onClose,
  lhcId,
  rowData,
  onMutationSelected,
  title = 'Cari Nomor Mutasi',
  submitLabel = 'Gunakan Mutasi',
  showUsedMutations = false,
}: SearchNomorMutasiModalProps) {
  const toast = useToast();
  const methods = useForm<{ mutationDate: string }>({
    defaultValues: { mutationDate: '' },
  });
  const [searchValue, setSearchValue] = useState('');
  const [mutationAmount, setMutationAmount] = useState('');
  const [searchError, setSearchError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [rows, setRows] = useState<SearchNomorMutasiRowData[]>([]);
  const [selectedRows, setSelectedRows] = useState<SearchNomorMutasiRowData[]>(
    []
  );
  const [isUsingMutation, setIsUsingMutation] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedMutation, setSelectedMutation] =
    useState<SearchNomorMutasiRowData | null>(null);
  const searchMutation = useSearchLHCItemMutationsMutation({
    onError: (error) => {
      showToast(toast, 'error', error.message || 'Gagal mencari nomor mutasi');
    },
  });
  const mutationDate = methods.watch('mutationDate');

  const resetAndClose = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedMutation(null);
    setSearchValue('');
    methods.reset({ mutationDate: '' });
    setMutationAmount('');
    setSearchError('');
    setHasSearched(false);
    setRows([]);
    setSelectedRows([]);
    onClose();
  }, [methods, onClose]);

  const handleSearch = useCallback(async () => {
    const trimmedSearch = searchValue.trim();

    if (trimmedSearch.length > 0 && trimmedSearch.length < MIN_SEARCH_LENGTH) {
      setSearchError('Nomor minimal 3 karakter untuk pencarian.');
      return;
    }

    setSearchError('');
    if (!rowData?.rowId) {
      showToast(
        toast,
        'error',
        'Data invoice tidak valid untuk pencarian mutasi'
      );
      return;
    }

    const mutationDateUnix = parseDateToUnixSeconds(mutationDate);
    const amount = parseAmountToNumber(mutationAmount);

    const result = await searchMutation.mutateAsync({
      lhcId,
      itemId: rowData.rowId,
      params: {
        ...(trimmedSearch
          ? { search_query: trimmedSearch, mutation_no: trimmedSearch }
          : {}),
        ...(mutationDateUnix ? { mutation_date: mutationDateUnix } : {}),
        ...(typeof amount === 'number' ? { amount } : {}),
      },
    });

    setRows(result.data.map(mapSearchItem));
    setHasSearched(true);
    setSelectedRows([]);
  }, [
    lhcId,
    mutationAmount,
    mutationDate,
    rowData?.rowId,
    searchMutation,
    searchValue,
    toast,
  ]);

  const handleSelectionChanged = useCallback(
    (event: { api: { getSelectedRows: () => SearchNomorMutasiRowData[] } }) => {
      setSelectedRows(event.api.getSelectedRows());
    },
    []
  );

  const handleUseMutation = useCallback(async () => {
    if (!selectedRows.length) return;

    const selectedMutationRow = selectedRows[0];
    const amountApplied = parseAmountToNumber(
      selectedMutationRow.mutationValue
    );

    if (typeof amountApplied !== 'number') {
      showToast(toast, 'error', 'Nominal mutasi tidak valid');
      return;
    }

    const payload: SearchNomorMutasiSelectionPayload = {
      bankMutationId: selectedMutationRow.mutationId,
      mutationNo: selectedMutationRow.mutationNo,
      amountApplied,
    };

    try {
      setIsUsingMutation(true);
      await onMutationSelected?.(payload);
      showToast(toast, 'success', 'Mutasi berhasil digunakan');
      resetAndClose();
    } catch (error) {
      showToast(
        toast,
        'error',
        error instanceof Error ? error.message : 'Gagal menggunakan mutasi'
      );
    } finally {
      setIsUsingMutation(false);
    }
  }, [onMutationSelected, resetAndClose, selectedRows, toast]);

  const handleOpenDrawer = useCallback((mutation: SearchNomorMutasiRowData) => {
    setSelectedMutation(mutation);
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedMutation(null);
  }, []);

  const columnDefs: ColDef<SearchNomorMutasiRowData>[] = [
    {
      colId: 'selection',
      headerName: '',
      width: 42,
      minWidth: 42,
      maxWidth: 42,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      pinned: 'left',
      sortable: false,
      resizable: false,
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    { field: 'mutationDate', headerName: 'Tgl. Mutasi', width: 110 },
    { field: 'mutationValue', headerName: 'Nilai Mutasi', width: 130 },
    { field: 'mutationNo', headerName: 'Nomor Mutasi', width: 130 },
    { field: 'bank', headerName: 'Bank', width: 90 },
    { field: 'memoLine', headerName: 'Memo Line', width: 260 },
    { field: 'remark', headerName: 'Remark', width: 170 },
    { field: 'remaining', headerName: 'Sisa', width: 100 },
    {
      colId: 'detail',
      headerName: '',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      resizable: false,
      cellRenderer: (params: { data: SearchNomorMutasiRowData }) => (
        <Center
          height="100%"
          cursor="pointer"
          onClick={() => handleOpenDrawer(params.data)}
        >
          <Icon as={ChevronRight} color="#0000A3" boxSize={5} />
        </Center>
      ),
    },
  ];

  const resultRows = hasSearched ? rows : [];
  const rowHeight = 42;
  const headerHeight = 48;
  const maxTableHeight = headerHeight + 5 * rowHeight;
  const dynamicHeight = resultRows.length
    ? `${Math.min(
        headerHeight + resultRows.length * rowHeight + 2,
        maxTableHeight
      )}px`
    : '46px';

  const showEmptySearchState =
    hasSearched && resultRows.length === 0 && !searchMutation.isPending;
  const showPreSearchState = !hasSearched;

  return (
    <>
      <Modal
        title={title}
        name="search-nomor-mutasi-modal"
        isOpen={isOpen}
        onClose={resetAndClose}
        size="5xl"
        isCentered
        hideAction
        scrollBehavior="outside"
        closeOnOverlayClick={!isDrawerOpen}
        modalBodyProps={{ pb: 6 }}
        modalHeaderProps={{
          fontFamily: 'heading',
          fontWeight: 500,
          fontSize: '20px',
          lineHeight: '100%',
          letterSpacing: '0%',
          color: '#373F41',
        }}
      >
        <MutationContextSummary
          rowData={rowData}
          showUsedMutations={showUsedMutations}
        />

        <MutationSearchFilters
          searchValue={searchValue}
          mutationAmount={mutationAmount}
          searchError={searchError}
          isLoading={searchMutation.isPending}
          methods={methods}
          onSearchValueChange={setSearchValue}
          onMutationAmountChange={(value) =>
            setMutationAmount(formatDisplayAmount(value))
          }
          onSearch={() => {
            void handleSearch();
          }}
          onClearSearch={() => {
            setSearchValue('');
            setSearchError('');
          }}
        />

        <CollectionSearchResultsSection<SearchNomorMutasiRowData>
          columnDefs={columnDefs}
          rowData={resultRows}
          isLoading={searchMutation.isPending}
          height={dynamicHeight}
          minHeight={dynamicHeight}
          gridOptions={{
            rowSelection: 'multiple',
            onSelectionChanged: handleSelectionChanged,
            suppressNoRowsOverlay: true,
            defaultColDef: {
              sortable: false,
              resizable: false,
            },
          }}
          sectionSx={{
            '& .ag-theme-diamond .ag-selection-checkbox': {
              margin: '0 !important',
            },
          }}
          emptyState={
            showEmptySearchState ? (
              <Center py={12}>
                <VStack spacing={4}>
                  <Text
                    fontWeight={500}
                    fontSize="20px"
                    lineHeight="100%"
                    letterSpacing="0%"
                    textAlign="center"
                    color="#000000"
                  >
                    Nomor Mutasi tidak ditemukan
                  </Text>
                  <Text
                    fontWeight={400}
                    fontSize="16px"
                    lineHeight="24px"
                    letterSpacing="0%"
                    textAlign="center"
                    color="#8E8E93"
                  >
                    Silakan cari nomor mutasi lain
                  </Text>
                </VStack>
              </Center>
            ) : null
          }
          preSearchState={
            showPreSearchState ? (
              <Center py={8}>
                <Text
                  fontWeight={500}
                  fontSize="16px"
                  lineHeight="100%"
                  letterSpacing="0%"
                  color="#8E8E93"
                >
                  Silahkan Cari Data Mutasi
                </Text>
              </Center>
            ) : null
          }
        />

        <Box display="flex" justifyContent="flex-end" mt={6}>
          <ChakraButton
            bgColor="#0000A3"
            color="white"
            _hover={{ bgColor: '#00008A' }}
            onClick={() => {
              void handleUseMutation();
            }}
            isLoading={isUsingMutation}
            isDisabled={!selectedRows.length || isUsingMutation}
          >
            {submitLabel}
          </ChakraButton>
        </Box>
      </Modal>

      <MutationDetailDrawer
        isOpen={isDrawerOpen}
        selectedMutation={selectedMutation}
        rowData={rowData}
        onClose={handleCloseDrawer}
      />
    </>
  );
}
